import { defineChatActions } from './actionsContext'
import { checkAuth } from './runtime'
import { BASE_URL } from '@//environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type {
  IChat,
  IEmployee,
  ISearchResults,
} from '@/modules/chat-matrix-fast/types/IChat'
import type { IEmployeeTreeDepartment } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { MATRIX_EMPLOYEE_QUERY_PARAMS } from '@/modules/chat-matrix-fast/constants/employeeApi'
import {
  getCachedEmployeeDisplayName,
  loadEmployeesTree,
  resolveEmployeeIdByMatrixUserId as lookupEmployeeIdByMatrixUserId,
} from '@/modules/chat-matrix-fast/services/employeeLookup'
import { httpGet } from '@/modules/chat-matrix-fast/services/httpClient'
import {
  isFallbackDialogTitle,
} from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import { pickFullerPersonTitle, pickRealChatTitle } from '@/modules/chat-matrix-fast/constants/chat'
import {
  normalizePhoneForMatrix,
  phoneFromMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixUserId'

import {
  getViewContacts,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'

/** Есть ли диалоги без нормального ФИО — тогда нужно дерево сотрудников. */
export function chatsNeedEmployeeTree(chats: IChat[]): boolean {
  return chats.some(chat => {
    if (chat.type !== 'dialog' && chat.type !== 'direct') {
      return false
    }

    const realTitle = pickRealChatTitle(chat)
    return !realTitle || isFallbackDialogTitle({ ...chat, title: realTitle })
  })
}

export const employeeActions = defineChatActions({
  // Дерево сотрудников для выбора участников чата
  async fetchEmployeesTree(): Promise<IEmployeeTreeDepartment[]> {
    return loadEmployeesTree()
  },

  // Поиск сотрудников по ФИО или должности через REST API
  async searchEmployees(query: string): Promise<IEmployee[]> {
    const term = query.trim()
    if (!term) {
      return []
    }

    try {
      const data = await httpGet<
          Array<{ id: string; full_name: string; position_name?: string; matrix_user_id?: string }>
          | { results: Array<{ id: string; full_name: string; position_name?: string; matrix_user_id?: string }> }
        >(
          `${BASE_URL}/api/employee/employees/`,
          { params: { search: term, ...MATRIX_EMPLOYEE_QUERY_PARAMS } },
        )

      const items = Array.isArray(data) ? data : (data.results ?? [])

      return items.map(item => ({
        id: String(item.id),
        full_name: item.full_name,
        email: null,
        department: null,
        position: item.position_name || null,
        can_create_dialog: true,
        matrix_user_id: item.matrix_user_id,
      }))
    } catch (error) {
      logger.error('chat_searchEmployees_error', {
        file: 'chatStore',
        function: 'searchEmployees',
        condition: String(error),
      })
      return []
    }
  },

  // Выполняет поиск чатов и предложений новых диалогов
  async searchChats(
    query: string,
  ): Promise<ISearchResults> {
    if (!checkAuth()) {
      return { chats: [], new_dialogs: [] }
    }

    if (!query.trim()) {
      this.searchResults = null

      return { chats: [], new_dialogs: [] }
    }

    this.isSearching = true
    try {
      const localChats = this.searchLocalChats(query.trim())
      const employees = await this.searchEmployees(query.trim())

      const combinedResults = {
        chats: localChats,
        new_dialogs: employees,
      }

      this.searchResults = combinedResults

      return combinedResults
    } catch (error) {
      logger.error('chat_searchChats_error', {
        file: 'chatStore',
        function: 'searchChats',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',

        message: 'Не удалось выполнить поиск',
        time: 7000,
      })
      throw error
    } finally {
      this.isSearching = false
    }
  },

  searchLocalChats(query: string): IChat[] {
    const searchTerm = query.toLowerCase().trim()

    return this.chats.filter(chat => {
      const titleMatch = chat.title?.toLowerCase().includes(searchTerm)

      const memberMatch = chat.members?.some(member => {
        const firstName = member.first_name?.toLowerCase() || ''
        const lastName = member.last_name?.toLowerCase() || ''
        const userName = member.user_name?.toLowerCase() || ''
        const fullName = `${firstName} ${lastName}`.trim() || userName

        return (
          firstName.includes(searchTerm)
            || lastName.includes(searchTerm)
            || userName.includes(searchTerm)
            || fullName.includes(searchTerm)
        )
      })

      return titleMatch || memberMatch
    })
  },

  // ФИО: dialog_member с бэка, иначе дерево сотрудников; Matrix mxid и телефон — не имена.
  // В title оставляем полное ФИО (шапка); список режет отчество через resolveChatDisplayTitle.
  resolveFallbackDialogTitle(chat: IChat): IChat {
    if (chat.type !== 'dialog' && chat.type !== 'direct') {
      const realTitle = pickRealChatTitle(chat)
      return realTitle && realTitle !== chat.title ? { ...chat, title: realTitle } : chat
    }

    const fullerTitle = pickFullerPersonTitle(chat)
    if (fullerTitle && !isFallbackDialogTitle({ ...chat, title: fullerTitle })) {
      return fullerTitle === (chat.title ?? '').trim() ? chat : { ...chat, title: fullerTitle }
    }

    const fromEmployees = getCachedEmployeeDisplayName({
      matrixUserId: chat.matrix_peer_id ?? chat.dialog_member?.matrix_user_id,
      employeeId: chat.dialog_member?.id,
    })
    if (fromEmployees) {
      return fromEmployees === (chat.title ?? '').trim() ? chat : { ...chat, title: fromEmployees }
    }

    const phone = phoneFromMatrixUserId(chat.matrix_peer_id)
    if (!phone) {
      return chat
    }

    const contact = getViewContacts().find(
      c => c.phoneNumber && normalizePhoneForMatrix(c.phoneNumber) === phone,
    )
    const resolvedName = contact?.fullName || this.searchResults?.new_dialogs?.find(
      e => e.phone_number && normalizePhoneForMatrix(e.phone_number) === phone,
    )?.full_name

    return resolvedName ? { ...chat, title: resolvedName } : chat
  },

  resolveFallbackDialogTitles(chats: IChat[]): IChat[] {
    return chats.map(chat => this.resolveFallbackDialogTitle(chat))
  },

  async resolveDialogTitlesWithEmployees(chats: IChat[]): Promise<IChat[]> {
    if (chatsNeedEmployeeTree(chats)) {
      await loadEmployeesTree()
    }

    return this.resolveFallbackDialogTitles(chats)
  },

  async resolveEmployeeInfo(employeeId: string): Promise<{
    phone?: string
    fullName?: string
    avatar?: string | null
    matrixUserId?: string
  }> {
    const fromContacts = getViewContacts().find(
      (contact: {
        id?: number | string
        employeeId?: string
        phoneNumber?: string
        fullName?: string
        avatar?: string
        matrixUserId?: string
      }) => String(contact.employeeId || contact.id) === String(employeeId),
    )
    if (fromContacts) {
      const phone = fromContacts.phoneNumber || undefined
      const matrixUserId = fromContacts.matrixUserId || undefined
      if (phone || matrixUserId) {
        return {
          phone,
          fullName: fromContacts.fullName,
          avatar: fromContacts.avatar ?? null,
          matrixUserId,
        }
      }
    }

    const { useApiStore } = await import('@/modules/apiStore/stores/apiStore')
    const apiStore = useApiStore()

    const fromEmployees = apiStore.employees?.find(
      employee => String(employee.id) === String(employeeId),
    )
    if (fromEmployees?.phone_number || fromEmployees?.matrix_user_id) {
      return {
        phone: fromEmployees.phone_number,
        fullName: fromEmployees.full_name,
        avatar: fromEmployees.avatar ?? null,
        matrixUserId: fromEmployees.matrix_user_id ?? undefined,
      }
    }

    const fromSearch = this.searchResults?.new_dialogs?.find(
      employee => String(employee.id) === String(employeeId),
    )
    if (fromSearch?.phone_number || fromSearch?.matrix_user_id) {
      return {
        phone: fromSearch.phone_number,
        fullName: fromSearch.full_name,
        avatar: fromSearch.avatar ?? null,
        matrixUserId: fromSearch.matrix_user_id ?? undefined,
      }
    }

    try {
      const data = await httpGet<{
        phone_number?: string
        full_name?: string
        first_name?: string
        last_name?: string
        avatar?: string | null
        matrix_user_id?: string | null
      }>(`${BASE_URL}/api/employee/employees/${employeeId}/`)

      return {
        phone: data.phone_number,
        fullName: data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
        avatar: data.avatar ?? null,
        matrixUserId: data.matrix_user_id ?? undefined,
      }
    } catch (error) {
      logger.error('chat_resolveEmployeeInfo_error', {
        file: 'chatStore',
        function: 'resolveEmployeeInfo',
        condition: String(error),
      })
      return {}
    }
  },

  async resolveEmployeeIdByMatrixUserId(matrixUserId: string): Promise<string | undefined> {
    const normalized = matrixUserId.trim()
    if (!normalized) {
      return undefined
    }

    for (const chat of this.chats) {
      const peerId = chat.dialog_member?.id
      if (chat.dialog_member?.matrix_user_id === normalized && peerId) {
        return String(peerId)
      }
    }

    return lookupEmployeeIdByMatrixUserId(normalized)
  },
})
